/**
 * severityOrder.ts — ordering + emphasis helpers for the Health board.
 *
 * The BFF is the single source of truth for STATUS: it computes each source's
 * roll-up (`SourceSnapshot.status`) and the board `overall` using the precedence
 * `down > degraded > unknown > ok`. This module does NOT recompute that roll-up —
 * it only turns that same precedence into a stable ORDERING so the broken thing
 * floats to the top of the board (design rubric §"one status-led focal per
 * screen"). The rank below is the ordering face of the exact same precedence.
 */

import type { SourceStatus } from '../../api/types';

/**
 * Sort rank for a status — LOWER sorts first. This is the ordering projection of
 * the roll-up precedence `down > degraded > unknown > ok` (worst floats up).
 * `unknown` (no-signal) deliberately outranks `ok` but sits below real faults.
 */
export const SEVERITY_RANK: Record<SourceStatus, number> = {
  down: 0,
  degraded: 1,
  unknown: 2,
  ok: 3,
};

/** A status counts as "broken / needs attention" for emphasis + focal purposes. */
export const isAttentionStatus = (status: SourceStatus): boolean =>
  status === 'down' || status === 'degraded';

/**
 * Stable severity sort: returns a NEW array ordered worst-first, preserving the
 * input order within each severity band (source/item order is the tiebreaker).
 *
 * Array.prototype.sort is stable in every supported runtime, but we key the
 * comparator on the original index as well so the intent is explicit and does
 * not silently depend on that guarantee.
 */
export const sortBySeverity = <T>(items: readonly T[], getStatus: (item: T) => SourceStatus): T[] =>
  items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const byRank = SEVERITY_RANK[getStatus(a.item)] - SEVERITY_RANK[getStatus(b.item)];
      return byRank !== 0 ? byRank : a.index - b.index;
    })
    .map(({ item }) => item);
